import { UseQueryResult } from "@tanstack/react-query";
import {
  DivergenceVerdict,
  PathDiff,
  ProcessValidationResult,
  RevisionDivergenceReport,
  useCompareRevisions,
  useValidateProcessRevision,
  useVerifyRevisions,
} from "@via/api/revisions-compare.api.tsx";
import { useProcessPublishedRevisions } from "@via/api/script-assets.api.tsx";
import { useProcessRevisionList } from "@via/pages/projects/pipelines/pipelines.api";
import { formatDateString } from "@via/utils/helpers.tsx";
import { JSX, useEffect, useMemo, useRef, useState } from "react";

/**
 * Compares two revisions of a process: what was authored on each side, diffed from the database,
 * with an optional, separate check of each side against what its git tag actually committed.
 *
 * Deliberately NOT gated on a git connection: both sides of the comparison render from the
 * database, so it works with no repository and no network. The parent form gates the tab itself
 * on `isRevisionsTabAvailable`, below, exported so that gate is unit testable without mounting
 * the whole edit form.
 */
export const MIN_REVISIONS_TO_COMPARE = 2;

export const isRevisionsTabAvailable = (revisionCount: number): boolean =>
  MIN_REVISIONS_TO_COMPARE <= revisionCount;

type RevisionRow = {
  id: number;
  rev_id?: number;
  rev_comment?: string;
  date_modified?: string | Date;
};

const OMITTED_REASON_LABEL: Record<string, string> = {
  "over-path-cap": "this path alone exceeded the per file cap",
  "over-comparison-cap": "the comparison exceeded its total cap",
};

const VERDICT_LABEL: Record<string, string> = {
  match: "Match",
  "generator-changed": "Generator changed",
  "content-changed": "Content changed",
  impossible: "Impossible",
  "not-verified": "Not verified",
};

/**
 * `impossible` carries `severity: "error"`: both attestations agree and the bytes still differ,
 * which `attributeDivergence` calls out as something that should not be possible. Rendering it in
 * the same gray as "Match" buries the one verdict that most needs a person's attention, so this
 * maps `severity` to a loud, bold red; `content-changed`'s `warn` gets amber, distinct from error
 * but still a step up from the gray every other, unremarkable verdict keeps.
 */
const VERDICT_SEVERITY_CLASS: Record<DivergenceVerdict["severity"], string> = {
  error: "font-bold text-red-700",
  warn: "text-amber-700",
  info: "text-gray-500",
};

/** A path whose diff exceeded a cap. Still visible, marked with its byte size and the reason. */
const PathDiffRow = ({ path }: { path: PathDiff }): JSX.Element => (
  <div className="rounded border border-gray-200 p-2">
    <div className="flex items-center gap-2 font-mono text-xs">
      <span className="rounded bg-gray-100 px-1.5 py-0.5 uppercase text-gray-600">
        {path.change}
      </span>
      <span className="text-gray-800">{path.path}</span>
    </div>
    {path.omitted ? (
      <p className="mt-1 text-xs text-amber-700">
        Diff omitted: {path.omitted.bytes} bytes,{" "}
        {OMITTED_REASON_LABEL[path.omitted.reason] ?? path.omitted.reason} (cap{" "}
        {path.omitted.cap} bytes).
      </p>
    ) : path.diff ? (
      <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap text-xs">
        {path.diff}
      </pre>
    ) : null}
  </div>
);

/**
 * One side's compact status line. Defaults to, and on any failure falls back to, "Not verified":
 * per the controller ruling, a verify failure is never an error state the user must clear.
 *
 * The divergence diff, when present, is the spec's "second view": reachable only from this line,
 * behind a toggle, never shown by default.
 */
const VerifyStatusLine = ({
  label,
  side,
  verifyQuery,
}: {
  label: string;
  side: "a" | "b";
  verifyQuery: UseQueryResult<RevisionDivergenceReport, Error>;
}): JSX.Element => {
  const [showDivergence, setShowDivergence] = useState(false);
  const verification = verifyQuery.isError
    ? undefined
    : verifyQuery.data?.[side];
  const kind = verification?.verdict.kind ?? "not-verified";
  const severity: DivergenceVerdict["severity"] =
    verification?.verdict.severity ?? "info";
  const message = verification?.verdict.message;
  const divergenceDiff = verification?.diff;

  return (
    <div className="text-xs text-gray-500">
      <span className="font-medium text-gray-700">{label}:</span>{" "}
      <span className={VERDICT_SEVERITY_CLASS[severity]}>
        {VERDICT_LABEL[kind] ?? kind}
      </span>
      {message ? <span className="ml-1 text-gray-400">({message})</span> : null}
      {divergenceDiff ? (
        <button
          type="button"
          className="ml-2 text-indigo-600 hover:underline"
          onClick={() => setShowDivergence(prev => !prev)}>
          {showDivergence ? "Hide divergence" : "View divergence"}
        </button>
      ) : null}
      {showDivergence && divergenceDiff ? (
        <div className="mt-1 space-y-1 border-t border-gray-100 pt-1">
          {divergenceDiff.paths.map(path => (
            <PathDiffRow key={path.path} path={path} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default function RevisionsTab({
  processId,
  canEditProcess,
}: {
  processId: number;
  canEditProcess: boolean;
}): JSX.Element {
  const { data: revisionListData } = useProcessRevisionList(String(processId));
  const revisionRows: RevisionRow[] = revisionListData?.revisions ?? [];
  // Sorted newest first. The list endpoint already returns this order, but sorting here does not
  // depend on that staying true.
  const revisions = useMemo(
    () => [...revisionRows].sort((x, y) => (y.rev_id ?? 0) - (x.rev_id ?? 0)),
    [revisionRows]
  );

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const didDefaultSelection = useRef(false);

  // Defaults to this revision against the previous one, once, the first time there are enough
  // revisions to default from. A ref, not a dependency check, is what keeps a later refetch (a
  // window focus, another mutation invalidating the list) from silently resetting a selection the
  // user already changed.
  useEffect(() => {
    if (
      didDefaultSelection.current ||
      revisions.length < MIN_REVISIONS_TO_COMPARE
    ) {
      return;
    }
    const currentIndex = revisions.findIndex(row => row.id === processId);
    let firstId: number;
    let secondId: number;
    if (-1 === currentIndex) {
      firstId = revisions[0].id;
      secondId = revisions[1].id;
    } else if (currentIndex + 1 < revisions.length) {
      // The next entry in a newest first list is the previous revision chronologically.
      firstId = revisions[currentIndex].id;
      secondId = revisions[currentIndex + 1].id;
    } else {
      // The open revision is the oldest one: there is no earlier revision, so pair it with the
      // only other option, the one immediately more recent.
      firstId = revisions[currentIndex].id;
      secondId = revisions[currentIndex - 1].id;
    }
    didDefaultSelection.current = true;
    setSelectedIds([firstId, secondId]);
  }, [revisions, processId]);

  const toggleSelected = (id: number): void => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(existing => existing !== id);
      }
      if (MIN_REVISIONS_TO_COMPARE <= prev.length) {
        return prev;
      }
      return [...prev, id];
    });
  };

  // Ordered older to newer regardless of click order, so the diff always reads as "what changed
  // going from the older side to the newer side" rather than depending on selection order.
  const pair = useMemo(() => {
    if (MIN_REVISIONS_TO_COMPARE !== selectedIds.length) {
      return null;
    }
    const rows = selectedIds
      .map(id => revisions.find(row => row.id === id))
      .filter((row): row is RevisionRow => undefined !== row);
    if (MIN_REVISIONS_TO_COMPARE !== rows.length) {
      return null;
    }
    const [older, newer] = [...rows].sort(
      (x, y) => (x.rev_id ?? 0) - (y.rev_id ?? 0)
    );
    return { aRow: older, bRow: newer };
  }, [selectedIds, revisions]);

  // The diff loads first, database only, and renders as soon as it answers. Verification is a
  // second, separate query: it never gates the diff above, and its own failure degrades to "not
  // verified" rather than an error the diff has to wait on.
  const compareQuery = useCompareRevisions(
    processId,
    pair?.aRow.id,
    pair?.bRow.id
  );
  const verifyQuery = useVerifyRevisions(
    processId,
    pair?.aRow.id,
    pair?.bRow.id
  );

  const { data: published, isError: publishedIsError } =
    useProcessPublishedRevisions(processId);
  // Commit id ships only where a repository actually exists for this process. No repository, or
  // the published check itself failing (commonly a read only viewer without write permission),
  // both mean there is nothing to show, so the whole column is omitted rather than left blank.
  const showCommitColumn = null != published && !publishedIsError;
  const tagByRevId = useMemo(() => {
    const map = new Map<number, string>();
    (published?.revisions ?? []).forEach(revision => {
      map.set(revision.revId, revision.tag);
    });
    return map;
  }, [published]);

  const validateRevision = useValidateProcessRevision();
  const [validationResults, setValidationResults] = useState<
    Record<number, ProcessValidationResult>
  >({});
  const handleValidate = (revisionId: number): void => {
    validateRevision.mutate(revisionId, {
      onSuccess: result => {
        setValidationResults(prev => ({ ...prev, [revisionId]: result }));
      },
    });
  };

  if (revisions.length < MIN_REVISIONS_TO_COMPARE) {
    // Defensive only: the parent form gates the tab itself on this same condition, so this
    // should be unreachable. Kept so a caller that skips the gate gets a plain message instead of
    // an empty compare view that teaches people the feature is broken.
    return (
      <p className="text-sm text-gray-500">
        At least two revisions are needed to compare.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Compare
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Revision
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Comment
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Date
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                Validation
              </th>
              {showCommitColumn ? (
                <th className="px-3 py-2 text-left font-medium text-gray-500">
                  Commit
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {revisions.map(row => {
              const isSelected = selectedIds.includes(row.id);
              const isCheckboxDisabled =
                !isSelected && MIN_REVISIONS_TO_COMPARE <= selectedIds.length;
              const validation = validationResults[row.id];
              const isValidating =
                validateRevision.isPending &&
                validateRevision.variables === row.id;
              const tag =
                undefined !== row.rev_id
                  ? tagByRevId.get(row.rev_id)
                  : undefined;

              return (
                <tr
                  key={row.id}
                  className={row.id === processId ? "bg-indigo-50" : undefined}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label={`Select revision ${row.rev_id}`}
                      checked={isSelected}
                      disabled={isCheckboxDisabled}
                      onChange={() => toggleSelected(row.id)}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-900">
                    Version {row.rev_id}
                    {row.id === processId ? (
                      <span className="ml-1 text-xs font-normal text-indigo-600">
                        (this revision)
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {row.rev_comment || "No comment"}
                  </td>
                  <td className="px-3 py-2 text-gray-500">
                    {row.date_modified
                      ? formatDateString(row.date_modified, "LL")
                      : ""}
                  </td>
                  <td className="px-3 py-2 text-gray-500">
                    {validation ? (
                      <span
                        className={
                          "passed" === validation.result
                            ? "text-green-700"
                            : "text-red-700"
                        }>
                        {"passed" === validation.result ? "Passed" : "Failed"} (
                        {validation.findings.length} finding
                        {1 === validation.findings.length ? "" : "s"})
                      </span>
                    ) : canEditProcess ? (
                      <button
                        type="button"
                        onClick={() => handleValidate(row.id)}
                        disabled={isValidating}
                        className="text-indigo-600 hover:underline disabled:text-gray-400">
                        {isValidating
                          ? "Validating..."
                          : "Not validated (validate)"}
                      </button>
                    ) : (
                      <span>Not validated</span>
                    )}
                  </td>
                  {showCommitColumn ? (
                    <td className="px-3 py-2 font-mono text-xs text-gray-500">
                      {tag ?? "Not exported"}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rounded-md border border-gray-200 p-3">
        {null === pair ? (
          <p className="text-sm text-gray-500">
            Select exactly two revisions to compare.
          </p>
        ) : (
          <div className="space-y-3">
            <VerifyStatusLine
              label={`Version ${pair.aRow.rev_id}`}
              side="a"
              verifyQuery={verifyQuery}
            />
            <VerifyStatusLine
              label={`Version ${pair.bRow.rev_id}`}
              side="b"
              verifyQuery={verifyQuery}
            />

            {compareQuery.isLoading ? (
              <p className="text-sm text-gray-500">Loading comparison...</p>
            ) : compareQuery.isError ? (
              <p className="text-sm text-red-600">
                The comparison could not be loaded. Try again.
              </p>
            ) : compareQuery.data ? (
              <div className="space-y-2">
                <p className="text-xs text-gray-500">
                  Comparing version {pair.aRow.rev_id} to version{" "}
                  {pair.bRow.rev_id}: {compareQuery.data.paths.length} path
                  {1 === compareQuery.data.paths.length
                    ? ""
                    : "s"} changed, {compareQuery.data.totalDiffBytes} bytes.
                </p>
                {0 === compareQuery.data.paths.length ? (
                  <p className="text-sm text-gray-500">No differences.</p>
                ) : (
                  compareQuery.data.paths.map(path => (
                    <PathDiffRow key={path.path} path={path} />
                  ))
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
